import { getAPIKey } from './apiKeyUtils';

/**
 * Test OpenAI API key validity
 */
export async function testOpenAIKey(): Promise<{ valid: boolean; error?: string }> {
  try {
    const apiKey = getAPIKey('openai');
    if (!apiKey) {
      return { valid: false, error: 'No OpenAI API key found' };
    }

    const response = await fetch('https://api.openai.com/v1/models', {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    });

    if (response.ok) {
      return { valid: true };
    } else {
      const errorData = await response.json().catch(() => ({}));
      return { 
        valid: false, 
        error: errorData.error?.message || `HTTP ${response.status}: ${response.statusText}` 
      };
    }
  } catch (error) {
    return { 
      valid: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Test Perplexity API key validity
 */
export async function testPerplexityKey(): Promise<{ valid: boolean; error?: string }> {
  try {
    const apiKey = getAPIKey('perplexity');
    if (!apiKey) {
      return { valid: false, error: 'No Perplexity API key found' };
    }

    // Simple test request to validate the key
    const response = await fetch('https://api.perplexity.ai/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'sonar',
        messages: [
          {
            role: 'user',
            content: 'Test message for API validation'
          }
        ],
        max_tokens: 10,
        temperature: 0.1
      }),
    });

    if (response.ok) {
      return { valid: true };
    } else {
      const errorData = await response.json().catch(() => ({}));
      return { 
        valid: false, 
        error: errorData.error?.message || `HTTP ${response.status}: ${response.statusText}` 
      };
    }
  } catch (error) {
    return { 
      valid: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Test both API keys and return a summary
 */
export async function testAllAPIKeys(): Promise<{
  openai: { valid: boolean; error?: string };
  perplexity: { valid: boolean; error?: string };
}> {
  const [openaiResult, perplexityResult] = await Promise.all([
    testOpenAIKey(),
    testPerplexityKey()
  ]);

  return {
    openai: openaiResult,
    perplexity: perplexityResult
  };
}